import { CalendarClock, CheckSquare } from "@tamagui/lucide-icons";
import { Timestamp } from "firebase/firestore";
import { action, computed, makeObservable, observable, override } from "mobx";
import type { Api } from "../../../core/apis";
import type { Stores } from "../../../core/stores";
import type { InfoRowItemProps } from "../../../ui/InfoRow";
import { log } from "../../../ui/log";
import BusinessModuleModel, {
  type BusinessModuleData,
  type BusinessModuleModelData,
  type CompletedData,
  type Snapshot,
} from "../BusinessModuleModel";
import type { BusinessModuleType } from "../types";
import {
  BusinessReminderModuleUI,
  ReminderModuleBlockUI,
  ReminderModuleFeedItemUI,
} from "./BusinessReminderModuleUI";

export type BusinessReminderModuleSettings = {
  offsetInDays: number | null;
  dateDue: Timestamp | null;
};

export type BusinessReminderModuleSnapshot = {
  dateDue: Timestamp;
  excludedUsers?: string[];
  completedBy?: CompletedData[];
};

export type BusinessReminderData = BusinessModuleData & {
  type: BusinessModuleType.Reminder;
  settings: BusinessReminderModuleSettings;
};

export type BusinessReminderModuleModelData =
  BusinessModuleModelData<BusinessReminderModuleSnapshot> &
    BusinessReminderData;

export default class BusinessReminderModuleModel extends BusinessModuleModel<
  BusinessReminderModuleSnapshot,
  BusinessReminderModuleModelData
> {
  hasDoneFlow = true;

  constructor(stores: Stores, api: Api, data: BusinessReminderModuleModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      settings: data.settings ?? {},
    });

    makeObservable(this);
  }

  @computed get calculatedDateWithoutDefault() {
    if (this.data.settings?.offsetInDays !== null) {
      if (this.businessPlan.data.eventInfo.date) {
        const updatedDateWithOffset = new Date(
          this.businessPlan.data.eventInfo.date.toDate(),
        );
        updatedDateWithOffset.setDate(
          updatedDateWithOffset.getDate() +
            (this.data.settings?.offsetInDays || 0),
        );
        return Timestamp.fromDate(updatedDateWithOffset);
      }
    }

    return this.data.settings?.dateDue;
  }

  @computed get calculatedDate() {
    return this.calculatedDateWithoutDefault ?? Timestamp.now();
  }

  @action async updateSnapshotWithoutSave() {
    this.data.snapshot = {
      dateDue: this.calculatedDate,
      completedBy: this.data.completedBy,
    };
  }

  updateModuleSnapshot() {
    this.updateSnapshotWithoutSave();
    this.api.modulesApi.update(this.uid, {
      snapshot: this.data.snapshot,
    });
  }

  @observable showFormSheet = false;

  @action toggleFormSheet() {
    this.showFormSheet = !this.showFormSheet;
  }

  @computed get infoRowItems() {
    const completedBy = this.data.completedBy;
    const totalCountUsers = this.businessPlan.usersCount;

    const items: InfoRowItemProps[] = [];

    if (completedBy.length) {
      items.push({
        icon: CheckSquare,
        text: `${completedBy.length ?? 0} / ${totalCountUsers}`,
      });
    }

    return items;
  }

  @action async updateDate(dateDue: Timestamp) {
    this.data.settings.dateDue = dateDue;

    this.updateSnapshotWithoutSave();
    this.save();
    this.sendTelemetryEvent("updated_dates");

    // TODO
    log.dev("fix email reminders!!");
    /*
    this.addEmailReminders(
      this.plan.uid,
      this.plan.data.title,
      this.uid,
      this.data.dateDue,
    );*/
  }

  @computed
  get activeUsers() {
    return this.businessPlan.visibleUsersSorted;
  }

  @override get isDone() {
    const users = this.activeUsers;
    return this.data.completedBy.length >= users.length;
  }

  @computed get isCompleted() {
    return this.isDone;
  }

  @computed get completedStatus() {
    return {
      count: this.data.completedBy.length,
      total: this.activeUsers.length,
    };
  }

  render() {
    return <BusinessReminderModuleUI businessModule={this} />;
  }

  renderBlock() {
    return <ReminderModuleBlockUI businessModule={this} />;
  }

  renderFeedItem() {
    return <ReminderModuleFeedItemUI businessModule={this} />;
  }

  renderIcon(size?: number) {
    return <CalendarClock size={size} />;
  }

  static is(
    module: BusinessModuleModel<Snapshot, BusinessModuleModelData<Snapshot>>,
  ): module is BusinessReminderModuleModel {
    return module instanceof BusinessReminderModuleModel;
  }
}
